import { useState, useRef } from 'react';
import './ImageUpload.css';

// Uses Cloudinary's free tier (25GB storage, 25GB bandwidth/month — plenty for launch).
// Setup (one-time, 5 minutes):
// 1. Create free account at cloudinary.com
// 2. Dashboard shows your "Cloud name" — copy it
// 3. Settings → Upload → Add upload preset → set "Signing Mode" to "Unsigned" → name it "halfprice_deals" → Save
// 4. Set these in Netlify environment variables:
//    REACT_APP_CLOUDINARY_CLOUD_NAME = your-cloud-name
//    REACT_APP_CLOUDINARY_UPLOAD_PRESET = halfprice_deals

const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'halfprice_deals';

export default function ImageUpload({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const handleFile = async (file) => {
    if (!file) return;
    if (!CLOUD_NAME) {
      setError('Image upload not configured yet — paste an image URL below instead.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB');
      return;
    }
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', UPLOAD_PRESET);
      formData.append('folder', 'halfprice-deals');

      const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data.secure_url) {
        onChange(data.secure_url);
      } else {
        setError('Upload failed — try a different image or paste a URL below.');
      }
    } catch {
      setError('Upload failed — check your connection and try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="image-upload">
      {value ? (
        <div className="image-upload-preview">
          <img src={value} alt="Deal preview" onError={(e) => { e.target.style.display = 'none'; }} />
          <button type="button" className="image-upload-remove" onClick={() => onChange('')}>Remove image</button>
        </div>
      ) : (
        <div
          className={`image-upload-dropzone ${uploading ? 'uploading' : ''}`}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => handleFile(e.target.files[0])}
          />
          {uploading ? (
            <span>Uploading…</span>
          ) : (
            <>
              <span style={{ fontSize: 28 }}>📷</span>
              <span>Click or drag a photo here</span>
              <span className="image-upload-hint">JPG or PNG, up to 5MB</span>
            </>
          )}
        </div>
      )}
      {error && <p className="image-upload-error">{error}</p>}
      <div className="image-upload-or">
        <span>or paste an image URL</span>
        <input
          type="text"
          className="form-control"
          placeholder="https://..."
          value={value && value.startsWith('http') && !value.includes('cloudinary') ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}
